import { ShiftManagementStatsQuery } from 'generated/resources/graphql'
import { computed, makeObservable } from 'mobx'

import { stringToDate } from 'modules/core/lib/dates'

import type RestDay from 'modules/shift_management/store/internal/models/RestDay'

import type Leave from './Leave'

export type TShiftConflict =
  ShiftManagementStatsQuery['shiftManagement']['statsConnection']['nodes'][number]['shiftConflicts'][number]

export interface Props {
  id: number
  employee_id: number
  start_at: string
  conflicting_shifts_ids: number[]
  conflicting_leaves_ids: number[]
  conflicting_rest_days_ids: number[]
}

class ShiftConflict {
  id: number
  employeeId: number
  startAt: moment.Moment
  conflictingShiftsIds: number[]
  conflictingLeavesIds: number[]
  conflictingRestDaysIds: number[]

  constructor(props: Props) {
    this.id = props.id
    this.employeeId = props.employee_id
    this.startAt = stringToDate(props.start_at, 'backend_datetime')
    this.conflictingShiftsIds = props.conflicting_shifts_ids
    this.conflictingLeavesIds = props.conflicting_leaves_ids
    this.conflictingRestDaysIds = props.conflicting_rest_days_ids

    makeObservable(this, {
      hasLeaveConflict: computed,
      hasRestDayConflict: computed,
      _conflictingLeavesIdsSet: computed,
      _conflictingRestDaysIdsSet: computed,
    })
  }

  static buildFromGraphQL(conflict: TShiftConflict): ShiftConflict {
    return new ShiftConflict({
      id: conflict?.id,
      employee_id: conflict?.employeeId,
      start_at: conflict.startAt,
      conflicting_shifts_ids: conflict.conflictingShiftsIds,
      conflicting_leaves_ids: conflict.conflictingLeavesIds,
      conflicting_rest_days_ids: conflict.conflictingRestDaysIds,
    })
  }

  get hasLeaveConflict(): boolean {
    return !!this.conflictingLeavesIds.length
  }

  get hasRestDayConflict(): boolean {
    return !!this.conflictingRestDaysIds.length
  }

  get _conflictingLeavesIdsSet(): Set<number> {
    return new Set(this.conflictingLeavesIds)
  }

  get _conflictingRestDaysIdsSet(): Set<number> {
    return new Set(this.conflictingRestDaysIds)
  }

  hasConflictWithLeave(leave: Leave): boolean {
    return this._conflictingLeavesIdsSet.has(leave.id)
  }

  hasConflictWithRestDay(restDay: RestDay): boolean {
    return this._conflictingRestDaysIdsSet.has(restDay.id)
  }
}

export default ShiftConflict
